import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Check,
  CreditCard,
  FileSignature,
  MapPin,
  Receipt,
  ScrollText,
  Users,
  UserRound,
} from "lucide-react";
import NavBar from "@/components/NavBar";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, formatPrice, formatClassWhen } from "@/lib/api";
import { buildCourse, buildEvent, buildBreadcrumb } from "@/lib/schemas";
import { CHECKOUT } from "@/constants/testIds";

const STEPS = [
  { id: 1, label: "Payment", icon: Receipt },
  { id: 2, label: "Attendee", icon: UserRound },
  { id: 3, label: "Add-Ons", icon: ScrollText },
  { id: 4, label: "Waiver", icon: FileSignature },
  { id: 5, label: "Review", icon: CreditCard },
];

const WAIVER_TEXT = `HOMESTEAD LIABILITY WAIVER & ASSUMPTION OF RISK

This Liability Waiver and Assumption of Risk ("Waiver") is entered into by the registering parent or legal guardian ("Participant") and Heather Mich, doing business as Graceful Garden Coaching ("Coach"), in connection with educational programming held on the working homestead property leased from Ron Hylton ("Landlord").

1. WORKING HOMESTEAD RISKS. Participant acknowledges that the premises is an actively operating homestead. Inherent and acknowledged risks include, but are not limited to: presence of domestic livestock and free-ranging poultry; venomous and non-venomous reptiles, insects, and arachnids native to Yavapai County, Arizona; uneven natural terrain, gravel, mulch, irrigation infrastructure, and rough fencing; cooking and food-handling activities involving heat, flame, dairy, gluten, and other potential allergens; sun exposure, dust, pollen, and dehydration risk. Participant agrees that these risks cannot be eliminated and assumes them voluntarily on behalf of themselves and the registered Student.

2. DESIGNATED LEARNING AREAS. All instruction occurs within clearly marked designated learning areas. Participants and attending adults agree to remain within these areas at all times, to refrain from approaching or handling animals not introduced to them by the Coach, to follow all posted signage, and to comply with reasonable instructions issued by the Coach or her designated assistants.

3. RELEASE OF HEATHER MICH AND RON HYLTON. To the fullest extent permitted by Arizona law, Participant, on behalf of themselves, the registered Student, and any minor accompanying them, hereby releases, waives, discharges, and covenants not to sue Heather Mich, Graceful Garden Coaching, landlord Ron Hylton, and their respective successors, agents, employees, volunteers, and assigns, from any and all liability, claims, demands, actions, and causes of action arising out of or related to any loss, damage, or injury — including death — that may be sustained by the Participant or the Student while on the premises, traveling to or from the premises, or participating in any program activity, regardless of whether caused in whole or in part by the negligence of the released parties.

4. MEDICAL CONSENT. In the event of a medical emergency, Participant authorizes Coach or her designee to summon medical assistance and consent on Participant's behalf to any reasonable emergency medical treatment for the Student until the Participant can be reached. Participant agrees to be financially responsible for any such treatment.

5. PHOTOGRAPHY. Coach may take photographs and short videos of activities for educational documentation and limited promotional use; faces will not be paired with last names.

6. ESA / CLASSWALLET. Participant acknowledges that ESA funding obligations are between the Participant and the State of Arizona; submission of the issued invoice to ClassWallet within seven (7) days is the Participant's responsibility.

7. ARRIVAL REQUIREMENT. Every attending adult must sign a printed copy of this waiver at the front welcome table upon arrival, regardless of whether they pre-registered.

8. SEVERABILITY & GOVERNING LAW. If any portion of this Waiver is held invalid, the remainder shall continue in full force. This Waiver is governed by the laws of the State of Arizona, venue Yavapai County.

By typing my full legal name below, I confirm that I have read this Waiver in its entirety, that I am 18 years of age or older, that I am the parent or legal guardian of the registered Student, and that I intend my typed name to constitute my legal signature.`;

export default function CheckoutPage() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("cancelled")) {
      toast.error("Payment cancelled. Your registration was not completed.");
    }
  }, [searchParams]);

  const { data: cls, isLoading, error } = useQuery({
    queryKey: ["class", classId],
    queryFn: async () => (await api.get(`/classes/${classId}`)).data,
  });

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    payment_method: "classwallet",
    parent_name: "",
    parent_email: "",
    parent_phone: "",
    parent_attending: false,
    parent_count: 0,
    allergies_notes: "",
    waiver_signature: "",
  });
  const [students, setStudents] = useState([
    { student_name: "", esa_contract_number: "" },
  ]);
  const [waiverScrolled, setWaiverScrolled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [discountCodeInput, setDiscountCodeInput] = useState("");
  const [discount, setDiscount] = useState(null); // {code, label, discount, applies_to}
  const [discountError, setDiscountError] = useState("");
  const [validatingCode, setValidatingCode] = useState(false);

  const directPayOnly = cls?.direct_pay_only === true;
  // Force direct pay + skip Payment step when the class disallows ClassWallet.
  // Must be declared BEFORE any early returns to satisfy Rules of Hooks.
  useEffect(() => {
    if (directPayOnly) {
      setForm((f) => (f.payment_method === "direct" ? f : { ...f, payment_method: "direct" }));
      setStep((s) => (s === 1 ? 2 : s));
    }
  }, [directPayOnly]);

  const addStudent = () => {
    if (students.length >= 5) return;
    setStudents([...students, { student_name: "", esa_contract_number: "" }]);
  };
  const removeStudent = (i) =>
    setStudents(students.length > 1 ? students.filter((_, idx) => idx !== i) : students);
  const setStudent = (i, key, val) => {
    const next = students.slice();
    next[i] = { ...next[i], [key]: val };
    setStudents(next);
  };

  if (error)
    return (
      <>
        <NavBar />
        <div className="max-w-2xl mx-auto p-12 text-center">
          <h1 className="font-heading text-3xl">Class not found</h1>
          <Link to="/" className="btn-pill btn-outline mt-6 inline-flex">
            Back home
          </Link>
        </div>
      </>
    );

  if (isLoading || !cls) {
    return (
      <>
        <NavBar />
        <div className="max-w-3xl mx-auto p-12">
          <div className="h-72 rounded-2xl bg-gg-surface border border-gg-border animate-pulse" />
        </div>
      </>
    );
  }

  const isDirectPay = form.payment_method === "direct";
  const parentCost = form.parent_attending ? Number(cls.price_parent) : 0;
  const studentUnit = Number(cls.price_student);
  const studentTotal = studentUnit * students.length;
  const total = studentTotal + parentCost;
  const stripeChargeNow = isDirectPay ? total : parentCost;
  const spotsRemaining = cls.spots_remaining ?? 0;
  const exceedsCapacity = students.length > spotsRemaining;

  const allStudentsFilled = students.every((s) => s.student_name.trim());
  const esaContracts = students.map((s) => s.esa_contract_number.trim()).filter(Boolean);
  const duplicateEsa = new Set(esaContracts).size !== esaContracts.length;

  const canProceedStep1 = !!form.payment_method;

  const canProceedStep2 =
    allStudentsFilled &&
    !duplicateEsa &&
    !exceedsCapacity &&
    form.parent_name.trim() &&
    form.parent_email.trim() &&
    form.parent_phone.trim();

  const canProceedStep4 =
    waiverScrolled &&
    form.waiver_signature.trim().toLowerCase() === form.parent_name.trim().toLowerCase() &&
    form.waiver_signature.trim().length >= 3;

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/registrations", {
        class_id: cls.id,
        students,
        parent_name: form.parent_name,
        parent_email: form.parent_email,
        parent_phone: form.parent_phone,
        parent_attending: form.parent_attending || form.parent_count > 0,
        parent_count: Number(form.parent_count || 0),
        allergies_notes: form.allergies_notes,
        waiver_signature: form.waiver_signature,
        payment_method: form.payment_method,
        discount_code: discount?.code || undefined,
        origin_url: window.location.origin,
      });
      if (data.requires_payment && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        navigate(`/success/${data.registration_id}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
      setSubmitting(false);
    }
  };

  const applyDiscount = async () => {
    const code = (discountCodeInput || "").trim().toUpperCase();
    if (!code) return;
    setValidatingCode(true);
    setDiscountError("");
    try {
      const { data } = await api.post("/discount-codes/validate", {
        code,
        class_id: cls.id,
        student_count: students.length,
        parent_count: Number(form.parent_count || 0),
        payment_method: form.payment_method,
      });
      if (data.valid) {
        setDiscount(data);
        toast.success(`Code applied — $${data.discount.toFixed(2)} off`);
      } else {
        setDiscount(null);
        setDiscountError(data.reason || "Invalid code");
      }
    } catch (e) {
      setDiscountError(e?.response?.data?.detail || "Could not validate code");
    } finally {
      setValidatingCode(false);
    }
  };

  const clearDiscount = () => {
    setDiscount(null);
    setDiscountCodeInput("");
    setDiscountError("");
  };

  return (
    <>
      <SEO
        title={`Register — ${cls.title}`}
        description={`Register for ${cls.title} in Chino Valley, AZ. ${formatClassWhen(cls)}. ${formatPrice(cls.price_student)} per student — ${cls.direct_pay_only ? "credit card at checkout" : "ClassWallet or credit card accepted"}.`}
        jsonLd={[
          buildCourse(cls),
          buildEvent(cls),
          buildBreadcrumb([
            { name: "Home", url: "/" },
            { name: "Upcoming Classes", url: "/#classes" },
            { name: cls.title, url: `/register/${cls.id}` },
          ]),
        ]}
      />
      <NavBar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gg-muted hover:text-gg-green">
          <ArrowLeft size={14} /> Back To Classes
        </Link>

        {/* Class summary card — full info before checkout */}
        <article className="mt-6 rounded-2xl border border-gg-border bg-gg-surface p-5 sm:p-7">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">
                Registering for
              </p>
              <h1
                className="font-heading text-2xl sm:text-3xl text-[var(--gg-text)] mt-1 leading-tight"
                data-testid="checkout-class-title"
              >
                {cls.title}
              </h1>
              <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-gg-muted">
                <p className="flex items-center gap-2">
                  <Calendar size={15} className="text-gg-green shrink-0" />
                  {formatClassWhen(cls)}
                </p>
                {cls.location && (
                  <p className="flex items-center gap-2">
                    <MapPin size={15} className="text-gg-green shrink-0" />
                    Chino Valley, AZ&nbsp;
                    <span className="text-xs text-gg-muted italic">— full address sent in your confirmation email</span>
                  </p>
                )}
                <p className="flex items-center gap-2">
                  <Users size={15} className="text-gg-green shrink-0" />
                  <span className={spotsRemaining <= 5 ? "text-[#9b6a26] font-medium" : ""}>
                    {spotsRemaining} of {cls.capacity_total} spots remaining
                  </span>
                </p>
              </div>
            </div>
            <div className="text-right shrink-0 border-t sm:border-t-0 sm:border-l border-gg-border sm:pl-5 pt-3 sm:pt-0">
              <p className="font-heading text-3xl text-gg-green leading-none">
                {formatPrice(studentUnit)}
              </p>
              <p className="text-xs text-gg-muted mt-1">per student</p>
              {Number(cls.price_parent) > 0 && cls.allow_parent_addon !== false && (
                <p className="text-xs text-gg-brown mt-2">
                  + {formatPrice(cls.price_parent)} optional parent add-on
                </p>
              )}
            </div>
          </div>

          {cls.description && (
            <div className="mt-5 border-t border-gg-border pt-4">
              <p className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold mb-2">
                About this class
              </p>
              <p className="text-sm text-[var(--gg-text)]/90 leading-relaxed whitespace-pre-wrap">
                {cls.description}
              </p>
            </div>
          )}
        </article>

        {/* Step indicator */}
        <ol className={`mt-8 grid gap-2 ${directPayOnly ? "grid-cols-4" : "grid-cols-5"}`}>
          {STEPS.filter((s) => !(directPayOnly && s.id === 1)).map((s) => {
            const active = step === s.id;
            const done = step > s.id;
            const Icon = s.icon;
            return (
              <li
                key={s.id}
                data-testid={CHECKOUT.stepIndicator(s.id)}
                className="flex flex-col items-center text-center"
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center border transition-colors ${
                    done
                      ? "bg-gg-green border-gg-green text-white"
                      : active
                      ? "bg-gg-green/10 border-gg-green text-gg-green"
                      : "bg-gg-surface border-gg-border text-gg-muted"
                  }`}
                >
                  {done ? <Check size={16} /> : <Icon size={16} />}
                </div>
                <span
                  className={`mt-2 text-[10px] sm:text-xs uppercase tracking-[0.16em] font-semibold ${
                    active ? "text-gg-green" : done ? "text-gg-brown" : "text-gg-muted"
                  }`}
                >
                  {s.label}
                </span>
              </li>
            );
          })}
        </ol>

        {/* Step content */}
        <div className="mt-8 rounded-2xl border border-gg-border bg-gg-surface p-6 sm:p-8">
          {step === 1 && (
            <StepPaymentMethod form={form} setForm={setForm} cls={cls} />
          )}
          {step === 2 && (
            <StepAttendee
              form={form}
              setForm={setForm}
              students={students}
              setStudent={setStudent}
              addStudent={addStudent}
              removeStudent={removeStudent}
              duplicateEsa={duplicateEsa}
              exceedsCapacity={exceedsCapacity}
              spotsRemaining={spotsRemaining}
              isDirectPay={isDirectPay}
              allowSiblings={cls.allow_siblings !== false}
              directPayOnly={directPayOnly}
            />
          )}
          {step === 3 && (
            <StepAddOns
              form={form}
              setForm={setForm}
              parentPrice={cls.price_parent}
              studentUnit={studentUnit}
              studentCount={students.length}
              studentTotal={studentTotal}
              total={total}
              isDirectPay={isDirectPay}
              allowParentAddon={cls.allow_parent_addon !== false}
              allowChaperones={cls.allow_chaperones !== false}
            />
          )}
          {step === 4 && (
            <StepWaiver
              form={form}
              setForm={setForm}
              waiverScrolled={waiverScrolled}
              setWaiverScrolled={setWaiverScrolled}
            />
          )}
          {step === 5 && (
            <StepPayment
              form={form}
              cls={cls}
              students={students}
              studentTotal={studentTotal}
              parentCost={parentCost}
              total={total}
              stripeChargeNow={stripeChargeNow}
              isDirectPay={isDirectPay}
              submitting={submitting}
              onSubmit={onSubmit}
              discount={discount}
              discountCodeInput={discountCodeInput}
              setDiscountCodeInput={setDiscountCodeInput}
              applyDiscount={applyDiscount}
              clearDiscount={clearDiscount}
              discountError={discountError}
              validatingCode={validatingCode}
            />
          )}
        </div>

        {/* Nav buttons — sticky bottom on mobile so parents don't need to scroll to advance */}
        {step < 5 && (
          <div className="mt-6 sticky bottom-0 z-30 -mx-4 sm:mx-0 sm:static bg-gg-surface/95 backdrop-blur-md sm:bg-transparent sm:backdrop-blur-none border-t border-gg-border sm:border-0 px-4 sm:px-0 py-3 sm:py-0 flex items-center justify-between gap-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }} data-testid="checkout-nav-bar">
            <button
              type="button"
              className="btn-pill btn-outline disabled:opacity-40 disabled:pointer-events-none"
              onClick={() => setStep((s) => Math.max(directPayOnly ? 2 : 1, s - 1))}
              disabled={step === (directPayOnly ? 2 : 1)}
              data-testid={CHECKOUT.backBtn}
            >
              <ArrowLeft size={14} /> Back
            </button>
            <button
              type="button"
              className="btn-pill btn-primary disabled:opacity-40 disabled:pointer-events-none flex-1 sm:flex-none justify-center"
              onClick={() => setStep((s) => Math.min(5, s + 1))}
              disabled={
                (step === 1 && !canProceedStep1) ||
                (step === 2 && !canProceedStep2) ||
                (step === 4 && !canProceedStep4)
              }
              data-testid={CHECKOUT.nextBtn}
            >
              Continue <ArrowRight size={14} />
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ---------- step components ----------

function StepPaymentMethod({ form, setForm, cls }) {
  const choose = (m) => setForm((f) => ({ ...f, payment_method: m }));
  const selected = form.payment_method;
  const cw = selected === "classwallet";
  const dp = selected === "direct";
  return (
    <div className="space-y-6">
      <header>
        <h2 className="font-heading text-2xl text-[var(--gg-text)]">How will you pay?</h2>
        <p className="text-sm text-gg-muted mt-1">
          Most families pay through Arizona ESA / ClassWallet. If your funds aren&apos;t going
          through ClassWallet (or you&apos;d just rather not wait for the invoice cycle),
          you can pay the full registration directly by credit card.
        </p>
      </header>

      <div className="grid sm:grid-cols-2 gap-4">
        <button
          type="button"
          onClick={() => choose("classwallet")}
          data-testid={CHECKOUT.paymentMethodClasswallet}
          className={`text-left rounded-2xl border p-5 sm:p-6 transition-all ${
            cw
              ? "border-gg-green bg-gg-green/8 ring-2 ring-gg-green/30"
              : "border-gg-border bg-gg-bg hover:border-gg-green/50"
          }`}
        >
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">
              ESA · ClassWallet
            </p>
            {cw && <Check className="text-gg-green" size={18} />}
          </div>
          <p className="font-heading text-xl text-[var(--gg-text)] mt-2">
            Pay via Arizona ESA invoice
          </p>
          <p className="text-sm text-gg-muted mt-2 leading-relaxed">
            You&apos;ll get a ClassWallet-ready PDF invoice per student. Upload to ClassWallet
            within <strong>7 days</strong> to secure the spot.
          </p>
          <p className="text-xs text-gg-brown font-semibold mt-3">
            {formatPrice(cls.price_student)} per student · billed through ClassWallet
          </p>
        </button>

        <button
          type="button"
          onClick={() => choose("direct")}
          data-testid={CHECKOUT.paymentMethodDirect}
          className={`text-left rounded-2xl border p-5 sm:p-6 transition-all ${
            dp
              ? "border-gg-green bg-gg-green/8 ring-2 ring-gg-green/30"
              : "border-gg-border bg-gg-bg hover:border-gg-green/50"
          }`}
        >
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">
              Credit card
            </p>
            {dp && <Check className="text-gg-green" size={18} />}
          </div>
          <p className="font-heading text-xl text-[var(--gg-text)] mt-2">
            Pay the full fee now
          </p>
          <p className="text-sm text-gg-muted mt-2 leading-relaxed">
            Charge runs immediately on Stripe. Spot is held for <strong>24 hours</strong>
            {" "}while you complete checkout. No ESA invoice needed.
          </p>
          <p className="text-xs text-gg-brown font-semibold mt-3">
            {formatPrice(cls.price_student)} per student · paid by credit card today
          </p>
          <p className="text-[11px] text-gg-green font-semibold mt-2 leading-snug">
            ✓ No processing fees — Stripe's fee is absorbed by us. What you see is what you pay.
          </p>
        </button>
      </div>
    </div>
  );
}

function StepAttendee({ form, setForm, students, setStudent, addStudent, removeStudent, duplicateEsa, exceedsCapacity, spotsRemaining, isDirectPay, allowSiblings = true, directPayOnly = false }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div className="space-y-6">
      <header>
        <h2 className="font-heading text-2xl text-[var(--gg-text)]">Attendee information</h2>
        <p className="text-sm text-gg-muted mt-1">
          {directPayOnly
            ? "Used on your receipt and waiver."
            : isDirectPay
            ? "Used on your receipt and waiver. ESA ID is optional when paying by credit card."
            : "Used on the ESA invoice. Each student gets their own ClassWallet-ready invoice keyed to their ESA ID."}
        </p>
      </header>

      {/* Students */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">
            Student{students.length > 1 ? "s" : ""} ({students.length})
          </p>
          <p className="text-xs text-gg-muted">{spotsRemaining} spot{spotsRemaining === 1 ? "" : "s"} available</p>
        </div>
        {students.map((s, i) => (
          <div key={i} className="rounded-xl border border-gg-border bg-gg-bg p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-gg-brown">Student {i + 1}</p>
              {students.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeStudent(i)}
                  className="text-xs text-[#B24C4C] hover:underline"
                  data-testid={`checkout-remove-student-${i}`}
                >
                  Remove
                </button>
              )}
            </div>
            <div className={`grid ${directPayOnly ? "" : "sm:grid-cols-2"} gap-3`}>
              <Field label="Student name">
                <Input
                  value={s.student_name}
                  onChange={(e) => setStudent(i, "student_name", e.target.value)}
                  placeholder={`Student ${i + 1} full name`}
                  data-testid={i === 0 ? CHECKOUT.studentName : `checkout-student-name-${i}`}
                  autoComplete="off"
                  name={`gg_student_name_${i}`}
                />
              </Field>
              {!directPayOnly && (
                <Field
                  label="ESA ID (optional)"
                  hint="Not required — but if you're using ClassWallet, providing it lets us pre-format the invoice + curriculum docs to match your ClassWallet account."
                >
                  <Input
                    value={s.esa_contract_number}
                    onChange={(e) => setStudent(i, "esa_contract_number", e.target.value)}
                    placeholder="ESA-2026-000000 (optional)"
                    data-testid={i === 0 ? CHECKOUT.esaContract : `checkout-esa-contract-${i}`}
                    autoComplete="off"
                    name={`gg_esa_contract_${i}`}
                  />
                </Field>
              )}
            </div>
          </div>
        ))}
        {students.length < 5 && allowSiblings && (
          <button
            type="button"
            onClick={addStudent}
            className="text-sm font-semibold text-gg-green hover:text-gg-brown inline-flex items-center gap-1"
            data-testid="checkout-add-student"
          >
            + Add another student
          </button>
        )}
        {duplicateEsa && !directPayOnly && (
          <p className="text-xs text-[var(--gg-error)]">Each student must have a distinct ESA ID.</p>
        )}
        {exceedsCapacity && (
          <p className="text-xs text-[var(--gg-error)]">
            Only {spotsRemaining} spot{spotsRemaining === 1 ? "" : "s"} remaining — please reduce the number of students.
          </p>
        )}

        {/* Sibling / under-5 policy — varies by allowSiblings */}
        {allowSiblings ? (
          <div className="rounded-xl border border-gg-border bg-gg-bg/60 p-4 text-xs leading-relaxed text-gg-muted">
            <p className="font-semibold text-[var(--gg-text)] mb-1">A note about siblings &amp; little ones</p>
            <p>
              Younger siblings are welcome if a paid spot is purchased for them — add another student above to reserve their spot.
              Lap-carried babies (not yet walking) are always free, no spot needed.
            </p>
            <p className="mt-1.5">
              <strong className="text-[var(--gg-text)]">Children under 5 must be shadowed at all times by their parent</strong> to help
              with hands-on activities and supervision.
            </p>
          </div>
        ) : (
          <div
            className="rounded-xl border border-gg-border bg-gg-bg/60 p-4 text-xs leading-relaxed text-gg-muted"
            data-testid="checkout-siblings-disabled-note"
          >
            <p className="font-semibold text-[var(--gg-text)] mb-1">One student per registration</p>
            <p>
              This class is limited to one student per checkout — please complete a separate registration for each sibling.
              Lap-carried babies (not yet walking) are always free, no spot needed.
            </p>
            <p className="mt-1.5">
              <strong className="text-[var(--gg-text)]">Children under 5 must be shadowed at all times by their parent</strong> to help
              with hands-on activities and supervision.
            </p>
          </div>
        )}
      </section>

      {/* Parent */}
      <section>
        <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold mb-3">
          Parent / Guardian
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Parent / Guardian name">
            <Input
              value={form.parent_name}
              onChange={set("parent_name")}
              placeholder="Parent's first & last name"
              data-testid={CHECKOUT.parentName}
              autoComplete="off"
              name="gg_parent_name_checkout"
            />
          </Field>
          <Field label="Parent email">
            <Input
              type="email"
              value={form.parent_email}
              onChange={set("parent_email")}
              data-testid={CHECKOUT.parentEmail}
              autoComplete="off"
              name="gg_parent_email_checkout"
            />
          </Field>
          <Field label="Parent phone">
            <Input
              value={form.parent_phone}
              onChange={set("parent_phone")}
              data-testid={CHECKOUT.parentPhone}
              autoComplete="off"
              name="gg_parent_phone_checkout"
            />
          </Field>
          <Field
            label="Allergies / dietary needs (optional)"
            hint="Anything Heather should know? Nut allergies, dairy-free, sensory considerations, mobility needs, etc."
            className="sm:col-span-2"
          >
            <textarea
              rows={2}
              value={form.allergies_notes}
              onChange={(e) => setForm((f) => ({ ...f, allergies_notes: e.target.value }))}
              data-testid="checkout-allergies"
              placeholder="e.g. peanut allergy, gluten-free, needs shade breaks"
              className="w-full rounded-md border border-gg-border bg-gg-bg px-3 py-2 text-sm focus:border-gg-green focus:outline-none"
              autoComplete="off"
              name="gg_allergies"
            />
          </Field>
        </div>
      </section>
    </div>
  );
}

function StepAddOns({ form, setForm, parentPrice, studentUnit, studentCount, studentTotal, total, isDirectPay, allowParentAddon = true, allowChaperones = true }) {
  // Force parent_count=0 when the class disallows the parent add-on. Uses useEffect
  // to keep setState out of the render body (avoids React's "cannot update during render" warning).
  useEffect(() => {
    if (!allowParentAddon && (Number(form.parent_count || 0) > 0 || form.parent_attending)) {
      setForm((f) => ({ ...f, parent_count: 0, parent_attending: false }));
    }
  }, [allowParentAddon, form.parent_count, form.parent_attending, setForm]);
  return (
    <div className="space-y-6">
      {allowParentAddon ? (
        <>
          <header>
            <h2 className="font-heading text-2xl text-[var(--gg-text)]">Parent add-on</h2>
            <p className="text-sm text-gg-muted mt-1">
              {allowChaperones ? "Chaperoning is always free. The add-on is for parents who want to fully participate" : "The add-on is for parents who want to fully participate"} in the cooking class portion — one fee per parent, regardless of how many of your students are registered.
            </p>
          </header>

          <div className="rounded-2xl border border-gg-border bg-gg-bg p-5 sm:p-6">
            <p className="font-heading text-xl text-[var(--gg-text)] leading-tight mb-1">
              How many parents will participate in the cooking class?
            </p>
            <p className="text-sm text-gg-muted">
              One fee per parent ({formatPrice(parentPrice)} each). Up to 2 parents can join{allowChaperones ? " — additional adults are welcome to chaperone for free." : "."}
            </p>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {[0, 1, 2].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, parent_count: n, parent_attending: n > 0 }))}
                  data-testid={`parent-count-${n}`}
                  className={`rounded-xl border-2 py-3 text-center transition-colors ${
                    Number(form.parent_count || 0) === n
                      ? "border-gg-green bg-gg-green text-white"
                      : "border-gg-border bg-gg-surface text-[var(--gg-text)] hover:border-gg-green"
                  }`}
                >
                  <p className="font-heading text-2xl">{n}</p>
                  <p className="text-xs uppercase tracking-[0.14em] mt-0.5">
                    {n === 0 ? (allowChaperones ? "None / chaperone only" : "None") : n === 1 ? "1 parent" : "2 parents"}
                  </p>
                  {n > 0 && (
                    <p className="text-xs mt-1 opacity-90">+ {formatPrice(parentPrice * n)}</p>
                  )}
                </button>
              ))}
            </div>
            {Number(form.parent_count || 0) > 0 && (
              <p className="text-xs text-gg-muted mt-3">
                {isDirectPay
                  ? "Bundled into your credit-card charge at checkout."
                  : "Parent fees are paid by credit card at checkout. Only the student portion is billed through ClassWallet."}
              </p>
            )}
          </div>
        </>
      ) : (
        <header data-testid="checkout-parent-addon-disabled">
          <h2 className="font-heading text-2xl text-[var(--gg-text)]">Almost done</h2>
          <p className="text-sm text-gg-muted mt-1">
            {allowChaperones
              ? "This class is students-only for participation, but you're welcome to stay as a chaperone at no extra cost."
              : "This class is students-only. Please drop off your student and return at pickup time."}
          </p>
        </header>
      )}

      <div className="rounded-2xl bg-gg-bg border border-gg-border p-5 sm:p-6">
        <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold mb-3">
          Running total
        </p>
        <div className="flex items-center justify-between text-sm">
          <span>
            Student workshop{isDirectPay ? "" : " (ESA)"}{studentCount > 1 ? ` × ${studentCount}` : ""}
          </span>
          <span>
            {studentCount > 1
              ? `${formatPrice(studentUnit)} × ${studentCount} = ${formatPrice(studentTotal)}`
              : formatPrice(studentTotal)}
          </span>
        </div>
        {Number(form.parent_count || 0) > 0 && (
          <div className="flex items-center justify-between text-sm mt-1">
            <span>
              Parent cooking add-on (CC){Number(form.parent_count) > 1 ? ` × ${form.parent_count}` : ""}
            </span>
            <span>{formatPrice(Number(parentPrice) * Number(form.parent_count || 0))}</span>
          </div>
        )}
        <div className="border-t border-gg-border my-3" />
        <div className="flex items-center justify-between">
          <span className="font-heading text-lg">Total</span>
          <span className="font-heading text-2xl text-gg-green">{formatPrice(total)}</span>
        </div>
        {isDirectPay && (
          <p className="text-xs text-gg-muted mt-2">
            You&apos;ll be redirected to Stripe to charge {formatPrice(total)} to your card on the next step.
          </p>
        )}
      </div>
    </div>
  );
}

function StepWaiver({ form, setForm, waiverScrolled, setWaiverScrolled }) {
  const boxRef = useRef(null);
  const onScroll = () => {
    const el = boxRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 12) {
      setWaiverScrolled(true);
    }
  };
  const matchesParent =
    form.waiver_signature.trim().length >= 3 &&
    form.waiver_signature.trim().toLowerCase() === form.parent_name.trim().toLowerCase();

  return (
    <div className="space-y-5">
      <header>
        <h2 className="font-heading text-2xl text-[var(--gg-text)]">
          Homestead liability waiver
        </h2>
        <p className="text-sm text-gg-muted mt-1">
          Scroll to the bottom, then type your full legal name to sign.
        </p>
      </header>

      <div
        ref={boxRef}
        onScroll={onScroll}
        className="waiver-box rounded-xl border border-gg-border bg-gg-bg p-5 text-sm leading-relaxed whitespace-pre-wrap text-[var(--gg-text)]/90 max-h-72 overflow-y-auto"
        data-testid={CHECKOUT.waiverScrollBox}
      >
        {WAIVER_TEXT}
      </div>

      <div
        className={`text-xs flex items-center gap-2 ${
          waiverScrolled ? "text-gg-green" : "text-gg-muted"
        }`}
      >
        <Check size={14} /> {waiverScrolled ? "Scrolled to bottom — you may sign" : "Please scroll to the bottom to unlock the signature field"}
      </div>

      <Field
        label="Type your full legal name to sign"
        hint={`Must match the parent name entered earlier (“${form.parent_name || "—"}”)`}
      >
        <Input
          value={form.waiver_signature}
          onChange={(e) =>
            setForm((f) => ({ ...f, waiver_signature: e.target.value }))
          }
          disabled={!waiverScrolled}
          placeholder={form.parent_name || "Your full legal name"}
          className="font-script text-2xl h-14"
          data-testid={CHECKOUT.waiverSignatureInput}
        />
      </Field>

      {form.waiver_signature && !matchesParent && (
        <p className="text-xs text-[var(--gg-error)]">
          Signature must exactly match the parent name from Step 1.
        </p>
      )}
    </div>
  );
}

function StepPayment({ form, cls, students, studentTotal, parentCost, total, stripeChargeNow, isDirectPay, submitting, onSubmit,
  discount, discountCodeInput, setDiscountCodeInput, applyDiscount, clearDiscount, discountError, validatingCode }) {
  const hasParent = Number(form.parent_count || 0) > 0;
  const parentCount = Number(form.parent_count || 0);
  const multi = students.length > 1;
  const discountAmount = discount?.discount || 0;
  // Show discount only on direct-pay (or parent-add-on) where it actually reduces the Stripe charge.
  // ClassWallet-only registrations still let the parent enter a code (heather adjusts the invoice manually).
  const effectiveStripeCharge = Math.max(0, stripeChargeNow - discountAmount);
  return (
    <div className="space-y-6">
      <header>
        <h2 className="font-heading text-2xl text-[var(--gg-text)]">Review &amp; submit</h2>
        <p className="text-sm text-gg-muted mt-1">
          {isDirectPay
            ? `You'll be redirected to a secure Stripe page to charge ${formatPrice(effectiveStripeCharge)} to your card.`
            : hasParent
            ? "You'll be redirected to a secure Stripe page to pay the parent portion."
            : `ESA-only registration. You'll receive ${multi ? "one ClassWallet-ready invoice per student" : "a ClassWallet-ready invoice"} next.`}
        </p>
      </header>

      {multi && (
        <div className="rounded-xl border border-gg-border bg-gg-bg p-4 text-sm">
          <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold mb-2">
            Students ({students.length})
          </p>
          <ul className="space-y-1">
            {students.map((s, i) => (
              <li key={i} className="flex items-center justify-between">
                <span>{s.student_name}</span>
                <span className="text-gg-muted font-mono text-xs">
                  {s.esa_contract_number ? `ESA #${s.esa_contract_number}` : "—"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div
        className="rounded-2xl border border-gg-border bg-gg-bg p-5 sm:p-6"
        data-testid={CHECKOUT.paymentSummary}
      >
        <div className="mb-4 pb-4 border-b border-gg-border">
          <label htmlFor="discount-code-input" className="block text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold mb-2">
            Discount code
          </label>
          {!discount ? (
            <div className="flex gap-2">
              <input
                id="discount-code-input"
                type="text"
                value={discountCodeInput}
                onChange={(e) => setDiscountCodeInput(e.target.value.toUpperCase())}
                placeholder="EARLYBIRD"
                className="flex-1 rounded-lg border border-gg-border px-3 py-2 text-sm bg-white uppercase"
                data-testid="checkout-discount-input"
                disabled={validatingCode}
              />
              <button
                type="button"
                onClick={applyDiscount}
                disabled={!discountCodeInput || validatingCode}
                className="btn-pill btn-outline disabled:opacity-40"
                data-testid="checkout-apply-discount-btn"
              >
                {validatingCode ? "…" : "Apply"}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between rounded-lg bg-gg-green/10 border border-gg-green/30 px-3 py-2">
              <div>
                <p className="text-sm font-semibold text-gg-green">✓ {discount.code} applied</p>
                <p className="text-xs text-gg-muted">{discount.label}</p>
              </div>
              <button
                type="button"
                onClick={clearDiscount}
                className="text-xs text-gg-muted hover:text-[#B24C4C]"
                data-testid="checkout-clear-discount-btn"
              >
                Remove
              </button>
            </div>
          )}
          {discountError && (
            <p className="text-xs text-[#B24C4C] mt-2" data-testid="checkout-discount-error">{discountError}</p>
          )}
        </div>
        <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold mb-3">
          Payment split
        </p>
        <Row
          label={multi ? `Student workshop × ${students.length}` : "Student workshop"}
          sub={
            isDirectPay
              ? "Credit card — charged now on Stripe"
              : multi ? "ESA — one invoice per student via ClassWallet" : "ESA — paid via ClassWallet invoice"
          }
          value={studentTotal}
          accent={isDirectPay ? "green" : "brown"}
        />
        {!isDirectPay && (
          <Row
            label="2.04% Transaction Fee"
            sub="ClassWallet processing — required per ClassWallet Direct Pay vendor terms"
            value={+(studentTotal * 0.0204).toFixed(2)}
            accent="brown"
          />
        )}
        {hasParent && (
          <Row
            label={`Parent cooking add-on${parentCount > 1 ? ` × ${parentCount}` : ""}`}
            sub="Credit card — paid now on Stripe"
            value={parentCost}
            accent="green"
          />
        )}
        {discount && (
          <Row
            label={`Discount: ${discount.code}`}
            sub={discount.label || "Code applied"}
            value={-discountAmount}
            accent="green"
          />
        )}
        <div className="border-t border-gg-border my-4" />
        <div className="flex items-center justify-between">
          <span className="font-heading text-lg">{isDirectPay ? "Total today" : "Total invoiced to ClassWallet"}</span>
          <span className="font-heading text-3xl text-gg-green">
            {formatPrice(isDirectPay ? effectiveStripeCharge : +(studentTotal * 1.0204).toFixed(2))}
          </span>
        </div>
        {!isDirectPay && (
          <div className="flex items-center justify-between text-sm mt-2 text-gg-muted">
            <span>{hasParent ? "Total charged to your card today" : "Charged to your card today"}</span>
            <span>{formatPrice(Math.max(0, parentCost - (discount?.applies_to === "classwallet" ? 0 : discountAmount)))}</span>
          </div>
        )}
        {!isDirectPay && !hasParent && parentCost === 0 && (
          <p className="text-xs text-gg-muted mt-2">
            Student portion is billed via your ClassWallet invoice{multi ? "s" : ""}.
          </p>
        )}
      </div>

      {!isDirectPay && (
        <div className="rounded-xl bg-[#D9933B]/10 border border-[#D9933B]/30 p-4 text-sm">
          <p className="font-semibold text-[#9b6a26]">7-day ClassWallet deadline</p>
          <p className="text-gg-muted mt-1">
            You&apos;ll have <strong>7 days</strong> from registration to upload {multi ? "each ESA invoice" : "the ESA invoice"} to ClassWallet — otherwise the student spot is released.
          </p>
        </div>
      )}
      {isDirectPay && (
        <div className="rounded-xl bg-gg-green/8 border border-gg-green/30 p-4 text-sm">
          <p className="font-semibold text-gg-green">Direct credit-card payment</p>
          <p className="text-gg-muted mt-1">
            Your spot is held for <strong>24 hours</strong> while you complete the Stripe checkout.
            Once payment clears, the spot is locked in and a paid receipt PDF — signed by Heather Mich — is emailed to you.
          </p>
        </div>
      )}

      <p className="text-xs text-gg-muted leading-relaxed">
        By completing registration you agree to our{" "}
        <a className="text-gg-green hover:underline" href="/terms" target="_blank" rel="noreferrer">Terms &amp; Refund Policy</a>
        {" "}and <a className="text-gg-green hover:underline" href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
        Cancellation: full refund &gt;14 days out, class credit 14–5 days out, no refund &lt;5 days.
      </p>

      <div className="sticky bottom-0 z-30 -mx-4 sm:mx-0 bg-gg-surface/95 backdrop-blur-md sm:bg-transparent sm:backdrop-blur-none border-t border-gg-border sm:border-0 px-4 sm:px-0 py-3 sm:py-0" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }} data-testid="checkout-submit-bar">
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          className="btn-pill btn-primary w-full text-base disabled:opacity-60"
          data-testid={CHECKOUT.submitBtn}
        >
          {submitting ? "Working…" : isDirectPay
            ? `Pay ${formatPrice(total)} & Complete Registration`
            : hasParent
            ? `Pay ${formatPrice(parentCost)} & Complete Registration`
            : multi
            ? `Complete Registration & Get ${students.length} Invoices`
            : "Complete Registration & Get My Invoice"}
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

function Row({ label, sub, value, accent }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div>
        <p className="text-sm font-medium text-[var(--gg-text)]">{label}</p>
        <p className="text-xs text-gg-muted">{sub}</p>
      </div>
      <span className={`font-heading text-xl ${accent === "green" ? "text-gg-green" : "text-gg-brown"}`}>
        {formatPrice(value)}
      </span>
    </div>
  );
}

function Field({ label, hint, children, className = "" }) {
  return (
    <Label className={`block ${className}`}>
      <span className="block text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold mb-2">
        {label}
      </span>
      {children}
      {hint && <span className="block text-xs text-gg-muted mt-1.5">{hint}</span>}
    </Label>
  );
}
